import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Icon, TooltipComponent } from "../../../components/Component";
import { Button, Card, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { Table } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import SearchInput from "../../form-components/SearchComponent";
import { useSelector } from "react-redux";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import { calendarApis } from "../../../api/Calendar";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { useDispatch } from "react-redux";
import TopMenu from "../../../route/topMenu";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import { useNavigate } from "react-router-dom";

function CalendarList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const columns: any = [
    {
      title: "Calendar Title",
      dataIndex: "calendarTitle",
      key: "calendarTitle",
      width: 280,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`draftStatus-${record?.id}`}
              onClick={() => {
                navigate(`/pms/calendar-detail/${record.calendarId}`, {
                  state: { calendarTitle: text, tab: "view" }
                });
              }}
            >
              {text}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={record?.projectStatus == "Draft" ? "info" : ""}
              id={`draftStatus-${record?.id}`}
              direction="right"
              text={
                record?.projectStatus == "Draft"
                  ? "Click To Continue Calendar Creation"
                  : "Click To View Calendar Details"
              }
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },

    {
      title: "Start Date",
      dataIndex: "calendarStartDate",
      key: "calendarStartDate",
      width: 200,
    },
    {
      title: "End Date",
      dataIndex: "calendarEndDate",
      key: "calendarEndDate",
      width: 200,
    },
    {
      title: "View WorkDays",
      dataIndex: "viewWorkDays",
      key: "viewWorkdays",
      width: 200,
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              onClick={() => {
                navigate(`/pms/calendar-detail/${record.calendarId}`, {
                  state: { calendarTitle: record.calendarTitle, tab: "workDays" }
                });
              }}
            >
              {"View"}
            </span>
          </>
        );
      },
    },
    {
      title: "View Work Hours",
      dataIndex: "viewWorkHours",
      key: "viewWorkHours",
      width: 200,
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              onClick={() => {
                navigate(`/pms/calendar-detail/${record.calendarId}`, {
                  state: { calendarTitle: record.calendarTitle, tab: "workHours" }
                });
              }}
            >
              {"View"}
            </span>
          </>
        );
      },
    },
    {
      title: "View Holidays",
      dataIndex: "viewHolidays",
      key: "viewHolidays",
      width: 200,
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              onClick={() => {
                navigate(`/pms/calendar-detail/${record.calendarId}`, {
                  state: { calendarTitle: record.calendarTitle, tab: "holidays" }
                });
              }}
            >
              {"View"}
            </span>
          </>
        );
      },
    },
    {
      title: "Associated Projects",
      dataIndex: "associatedProjects",
      key: "associatedProjects",
      width: 200,
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              onClick={() => {
                navigate(`/pms/calendar-detail/${record.calendarId}`, {
                  state: { calendarTitle: record.calendarTitle, tab: "associatedProjects" }
                });
              }}
            >
              {"View"}
            </span>
          </>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 210,
      fixed: "right",
      render: (text: any) => {
        return (
          <div className="justify-start align-center">
            <h6
              className={`tb-status ms-1`}
              style={{
                color: `${text == "Active" ? "green" : "red"}`,
                fontSize: "14px",
              }}
            >
              {text == "Active" ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )}{" "}
              {text}
            </h6>
          </div>
        );
      },
    },
  ];
  const methods = useForm({
    mode: "onChange",
  });
  const {
    formState: { errors },
  } = methods;
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],

    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;
      const apiResponse = await calendarApis.listCalendars({
        page: current,
        pageSize: pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
      });

      const { data, status } = apiResponse;
      if (status) {
        const { list, count } = data;
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));

        setDataSource((prev: any) => (append ? [...prev, ...list] : list));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId // Last item ID for backward direction
            : dataSource[0]?.paginationId; // First item ID for forward direction

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  const refreshListView = () => {
    try {
      setPagination({
        current: 1,
        pageSize: 10,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData({
        pageDiff: 0,
      }); // calling for initial data load , if the view type is table
    } catch (error) {}
  };

  useEffect(() => {
    dispatch(updateMenuData(TopMenu?.activeProjects));
    refreshListView();
  }, []);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  return (
    <>
      <Head title={"Calendar"} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              <FormProvider {...methods}>
                <form>
                  <div
                    className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                    style={{ minHeight: "40px", border: "0px" }}
                  >
                    <div className="nk-ibx-head-actions w-45 ms-1">
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                        <div className={`w-100 m-0 `}>
                          <SearchInput
                            name="search"
                            placeholder="Search..."
                            width="100%"
                            prefixIcon={<SearchOutlined />}
                            onDebouncedSearch={async (value: any) => {
                              setsearchQuery(value);
                              setPagination({
                                ...pagination,
                                current: 1,
                              });
                              await fetchData({
                                oldPage: 1,
                                current: 1,
                                sortOrder: "DESC",
                                pageSize: pagination.pageSize.toString(),
                                direction: "P",
                                pageDiff: 0,
                                paginationId: null,
                                searchQuery: value,
                              });
                            }}
                            debounceDelay={300} // Adjust delay as needed
                          />
                        </div>
                      </ul>
                    </div>
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="dropdown-toggle btn btn-icon btn-trigger"
                            onClick={(ev) => {
                              refreshListView();
                            }}
                          >
                            <Icon name="redo"></Icon>
                          </DropdownToggle>
                        </UncontrolledDropdown>
                      </li>
                      <li className="pt-0 pb-0">
                        <AddAccess moduleName="Calendar">
                          <Button
                            className="toggle"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              navigate("/pms/calendar-creation");
                            }}
                          >
                            <Icon name={"plus"} />
                            <span>Add Calendar</span>
                          </Button>
                        </AddAccess>
                      </li>
                    </ul>
                  </div>
                </form>
              </FormProvider>

              <div className="card-inner card-inner-lg p-0">
                <div className="content clearfix">
                  <div
                    style={{
                      width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
                      margin: "10px auto",
                    }}
                  >
                    <SkeletonTable
                      loading={loading}
                      columns={columns as SkeletonTableColumnsType[]}
                    >
                      <Table
                        rowKey="calendarId"
                        columns={columns}
                        dataSource={dataSource}
                        pagination={pagination}
                        onChange={tableOnChange}
                        bordered
                        size="middle"
                        scroll={{
                          x: 1100,
                        }}
                      />
                    </SkeletonTable>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
}

export default CalendarList;
